import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BaseUrl } from "../../../../BaseUrl";
import { UserContext } from "../../../../context/UserContext";
import { FaCalendarAlt, FaMapMarkerAlt, FaHeart, FaRegHeart, FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import useLike from "../../../../hooks/useLike";
import BeigeArrowPrev from "../../../../img/icons/BeigeArrowPrev.png";
import BeigeArrowNext from "../../../../img/icons/BeigeArrowNext.png";
import PurpleArrowPrev from "../../../../img/icons/PurplArrowPrev.png";
import PurpleArrowNext from "../../../../img/icons/PurplArrowNext.png";
import "./EventsSection.css";

const getOrdinal = (n) => {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
};

const EventCard = ({ event, onClick, isPast = false, isLiked, onHeartClick }) => {
  const navigate = useNavigate();
  const titleChars = event.title?.slice(0, 2).toUpperCase() || "EV";

  // Use averageRating from API (calculated from Feedback reviews)
  const averageRating = isPast && event.averageRating ? event.averageRating : null;

  const handleHeartClick = (e) => {
    e.stopPropagation(); // Prevent card click
    onHeartClick();
  };

  return (
    <Col xs={6} sm={6} lg={3} className="mb-3">
      <div
        className={`events-card ${isPast ? 'events-card--past' : 'events-card--future'}`}
        onClick={onClick}
      >
        <div className="events-card__image">
          {event.imageUrl ? (
            <img src={event.imageUrl} alt={event.title} />
          ) : (
            <span className="events-card__initials">{titleChars}</span>
          )}
          {/* Coming Up badge for future events */}
          {!isPast && (
            <span className="events-card__coming-up">Coming Up</span>
          )}
          {/* Heart icon */}
          <button
            className="events-card__heart"
            onClick={handleHeartClick}
            aria-label="Save to favorites"
          >
            {isLiked ? <FaHeart /> : <FaRegHeart />}
          </button>
          {/* Rating stars for past events */}
          {isPast && averageRating && (
            <div className="events-card__rating">
              {[...Array(5)].map((_, i) => {
                const fullStars = Math.floor(averageRating);
                const hasHalf = averageRating % 1 === 0.5;

                if (i < fullStars) {
                  return <FaStar key={i} className="star-filled" />;
                } else if (i === fullStars && hasHalf) {
                  return <FaStarHalfAlt key={i} className="star-filled" />;
                } else {
                  return <FaRegStar key={i} className="star-empty" />;
                }
              })}
            </div>
          )}
        </div>
        <div className="events-card__content">
          <h5 className="events-card__title">{event.title}</h5>
          <p className="events-card__location">
            <FaMapMarkerAlt className="events-card__icon" />
            {event.location || "Location TBA"}
          </p>
          <p className="events-card__date">
            <FaCalendarAlt className="events-card__icon" />
            {new Date(event.startDateTime).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
            <span className="events-card__year">
              {new Date(event.startDateTime).getFullYear()}
            </span>
          </p>
          {isPast && event.highlightTags?.length > 0 && (
            <div className="events-card__tags">
              {event.highlightTags.slice(0, 2).map((tag, idx) => (
                <span
                  key={idx}
                  className={`events-card__tag events-card__tag--${tag.key}`}
                  title={tag.tooltip}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* Edition badge for future events */}
        {!isPast && (
          <span
            className={`events-card__edition ${event.edition && event.edition > 1 ? 'events-card__edition--returning' : 'events-card__edition--new'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (event.edition && event.edition > 1 && event.eventSeries) {
                // Returning edition -> show all editions of this series
                navigate(`/events?series=${encodeURIComponent(event.eventSeries)}`);
              } else {
                // New event -> show events by same organizer
                const organizer = event.organizers?.[0] || event.organizer;
                if (organizer) {
                  navigate(`/events?organizer=${encodeURIComponent(organizer)}`);
                }
              }
            }}
          >
            {event.edition && event.edition > 1
              ? <><span className="events-card__edition-num">{getOrdinal(event.edition)}</span> Edition</>
              : <>{'\u{1F331}'} New</>}
          </span>
        )}
      </div>
    </Col>
  );
};

const EventsSection = () => {
  const [allFutureEvents, setAllFutureEvents] = useState([]);
  const [allPastEvents, setAllPastEvents] = useState([]);
  const [futureStartIndex, setFutureStartIndex] = useState(0);
  const [pastStartIndex, setPastStartIndex] = useState(0);
  const [loadingFuture, setLoadingFuture] = useState(true);
  const [loadingPast, setLoadingPast] = useState(true);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const { toggleLike, isLiked } = useLike({
    entityType: 'event',
    isLoggedIn: () => !!user,
    onAuthRequired: () => navigate('/login'),
  });

  // Responsive items per page
  const [itemsPerPage, setItemsPerPage] = useState(4);

  useEffect(() => {
    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      if (width < 576) {
        setItemsPerPage(2);
      } else if (width < 992) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(4);
      }
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  // Reset pagination when items per page changes
  useEffect(() => {
    setFutureStartIndex(0);
    setPastStartIndex(0);
  }, [itemsPerPage]);

  useEffect(() => {
    // Build category query string from user's dance styles
    const categoryParams = user?.dances?.length
      ? user.dances.map(d => `category=${encodeURIComponent(d.style)}`).join('&')
      : '';

    const fetchFutureEvents = async () => {
      try {
        const url = `${BaseUrl}/api/v1/events/future-events${categoryParams ? `?${categoryParams}` : ''}`;
        const response = await axios.get(url);
        let eventsData = [];

        if (response.data?.success && Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          eventsData = response.data;
        }

        // If filtered by styles returned no results, fall back to all events
        if (eventsData.length === 0 && categoryParams) {
          const fallback = await axios.get(`${BaseUrl}/api/v1/events/future-events`);
          if (fallback.data?.success && Array.isArray(fallback.data.data)) {
            eventsData = fallback.data.data;
          } else if (Array.isArray(fallback.data)) {
            eventsData = fallback.data;
          }
        }

        const sorted = eventsData
          .sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime));

        setAllFutureEvents(sorted);
      } catch (err) {
        console.error("Error fetching future events:", err);
      } finally {
        setLoadingFuture(false);
      }
    };

    const fetchPastEvents = async () => {
      try {
        const url = `${BaseUrl}/api/v1/events/past-events${categoryParams ? `?${categoryParams}` : ''}`;
        const response = await axios.get(url);
        let eventsData = [];

        if (response.data?.success && Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          eventsData = response.data;
        }

        // If filtered by styles returned no results, fall back to all events
        if (eventsData.length === 0 && categoryParams) {
          const fallback = await axios.get(`${BaseUrl}/api/v1/events/past-events`);
          if (fallback.data?.success && Array.isArray(fallback.data.data)) {
            eventsData = fallback.data.data;
          } else if (Array.isArray(fallback.data)) {
            eventsData = fallback.data;
          }
        }

        const sorted = eventsData
          .sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime));

        setAllPastEvents(sorted);
      } catch (err) {
        console.error("Error fetching past events:", err);
      } finally {
        setLoadingPast(false);
      }
    };

    fetchFutureEvents();
    fetchPastEvents();
  }, [user]);

  // Get visible events based on current index
  const visibleFutureEvents = allFutureEvents.slice(futureStartIndex, futureStartIndex + itemsPerPage);
  const visiblePastEvents = allPastEvents.slice(pastStartIndex, pastStartIndex + itemsPerPage);

  // Navigation handlers for future events
  const handleFuturePrev = () => {
    setFutureStartIndex(Math.max(0, futureStartIndex - itemsPerPage));
  };

  const handleFutureNext = () => {
    if (futureStartIndex + itemsPerPage < allFutureEvents.length) {
      setFutureStartIndex(futureStartIndex + itemsPerPage);
    }
  };

  // Navigation handlers for past events
  const handlePastPrev = () => {
    setPastStartIndex(Math.max(0, pastStartIndex - itemsPerPage));
  };

  const handlePastNext = () => {
    if (pastStartIndex + itemsPerPage < allPastEvents.length) {
      setPastStartIndex(pastStartIndex + itemsPerPage);
    }
  };

  return (
    <section className="events-section">
      {/* Future Events - Top Row */}
      <div className="events-section__future">
        <Container>
          <div className="events-section__row-header">
            <div className="events-section__title-group">
              <h3 className="events-section__row-title">Coming Up</h3>
              <p className="events-section__row-subtitle">Don't miss these upcoming events</p>
            </div>
            <div className="events-section__nav-group">
              {allFutureEvents.length > itemsPerPage && (
                <div className="events-section__arrows events-section__arrows--beige">
                  <button
                    className="events-section__arrow"
                    onClick={handleFuturePrev}
                    disabled={futureStartIndex === 0}
                    aria-label="Previous events"
                  >
                    <img src={BeigeArrowPrev} alt="Previous" />
                  </button>
                  <button
                    className="events-section__arrow"
                    onClick={handleFutureNext}
                    disabled={futureStartIndex + itemsPerPage >= allFutureEvents.length}
                    aria-label="Next events"
                  >
                    <img src={BeigeArrowNext} alt="Next" />
                  </button>
                </div>
              )}
              <button
                className="events-section__view-all"
                onClick={() => navigate("/events?tab=upcoming")}
              >
                View All
              </button>
            </div>
          </div>

          {loadingFuture ? (
            <div className="events-section__loading">
              <Spinner animation="border" variant="secondary" />
              <p>Loading events...</p>
            </div>
          ) : visibleFutureEvents.length > 0 ? (
            <Row>
              {visibleFutureEvents.map((event) => (
                <EventCard
                  key={event._id}
                  event={event}
                  onClick={() => navigate(`/event/${event._id}`)}
                  isLiked={isLiked(event._id)}
                  onHeartClick={() => toggleLike(event._id)}
                />
              ))}
            </Row>
          ) : (
            <p className="events-section__empty">No upcoming events at this time.</p>
          )}
        </Container>
      </div>

      {/* Past Events - Bottom Row */}
      <div className="events-section__past">
        <Container>
          <div className="events-section__row-header">
            <div className="events-section__title-group">
              <h3 className="events-section__row-title">Past Events</h3>
              <p className="events-section__row-subtitle">See what dancers rated highest</p>
            </div>
            <div className="events-section__nav-group">
              {allPastEvents.length > itemsPerPage && (
                <div className="events-section__arrows events-section__arrows--purple">
                  <button
                    className="events-section__arrow"
                    onClick={handlePastPrev}
                    disabled={pastStartIndex === 0}
                    aria-label="Previous events"
                  >
                    <img src={PurpleArrowPrev} alt="Previous" />
                  </button>
                  <button
                    className="events-section__arrow"
                    onClick={handlePastNext}
                    disabled={pastStartIndex + itemsPerPage >= allPastEvents.length}
                    aria-label="Next events"
                  >
                    <img src={PurpleArrowNext} alt="Next" />
                  </button>
                </div>
              )}
              <button
                className="events-section__view-all"
                onClick={() => navigate("/events?tab=past")}
              >
                View All
              </button>
            </div>
          </div>

          {loadingPast ? (
            <div className="events-section__loading">
              <Spinner animation="border" variant="secondary" />
              <p>Loading events...</p>
            </div>
          ) : visiblePastEvents.length > 0 ? (
            <Row>
              {visiblePastEvents.map((event) => (
                <EventCard
                  key={event._id}
                  event={event}
                  isPast={true}
                  onClick={() => navigate(`/event/${event._id}`)}
                  isLiked={isLiked(event._id)}
                  onHeartClick={() => toggleLike(event._id)}
                />
              ))}
            </Row>
          ) : (
            <p className="events-section__empty">No past events to display.</p>
          )}
        </Container>
      </div>
    </section>
  );
};

export default EventsSection;
